import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Импорт компонентов
import Login from '@/views/Login.vue'
import Dashboard from '@/views/Dashboard.vue'
import DashboardList from '@/views/DashboardList.vue'

// Определение маршрутов
const routes = [
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      requiresGuest: true,
      title: 'Вход в систему'
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: {
      requiresAuth: true,
      title: 'Главный дэшборд'
    }
  },
  {
    path: '/dashboards',
    name: 'DashboardList',
    component: DashboardList,
    meta: {
      requiresAuth: true,
      title: 'Мои дэшборды'
    }
  },
  {
    path: '/data/:collection',
    name: 'DataView',
    component: () => import('@/views/DataView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Просмотр данных'
    }
  },
  // НОВЫЕ РОУТЫ ДЛЯ ДЕТАЛЬНОГО ПРОСМОТРА
  {
    path: '/data/leads/:id',
    name: 'LeadDetails',
    component: () => import('@/views/details/LeadDetails.vue'),
    meta: {
      requiresAuth: true,
      title: 'Детали лида'
    }
  },
  {
    path: '/data/deals/:id',
    name: 'DealDetails',
    component: () => import('@/views/details/DealDetails.vue'),
    meta: {
      requiresAuth: true,
      title: 'Детали сделки'
    }
  },

  // === НОВЫЕ РОУТЫ ДЛЯ ОТЧЕТОВ ===

  {
    path: '/reports',
    redirect: '/reports/leads'
  },
  {
    path: '/reports/leads',
    name: 'LeadReport',
    component: () => import('@/views/reports/LeadReport.vue'),
    meta: {
      requiresAuth: true,
      title: 'Отчет по лидам'
    }
  },
  {
    path: '/reports/leads/comparison',
    name: 'LeadComparison',
    component: () => import('@/views/reports/LeadComparison.vue'),
    meta: {
      requiresAuth: true,
      title: 'Отчет по динамике изменений лидов'
    }
  },
  {
    path: '/reports/leads/changes',
    name: 'LeadChanges',
    component: () => import('@/views/reports/LeadChanges.vue'),
    meta: {
      requiresAuth: true,
      title: 'Отчет по изменениям лидов'
    }
  },
  {
    path: '/reports/leads/changes/form',
    name: 'LeadChangesForm',
    component: () => import('@/views/reports/LeadChangesForm.vue'),
    meta: {
      requiresAuth: true,
      title: 'Изменения лидов по менеджерам'
    }
  },
  {
    path: '/reports/leads/weekly',
    name: 'LeadsWeeklyReport',
    component: () => import('@/views/reports/PeriodicReport.vue'),
    meta: {
      requiresAuth: true,
      title: 'Еженедельный отчёт',
      periodicKind: 'weekly'
    }
  },
  {
    path: '/reports/leads/monthly',
    name: 'LeadsMonthlyReport',
    component: () => import('@/views/reports/PeriodicReport.vue'),
    meta: {
      requiresAuth: true,
      title: 'Ежемесячный отчёт',
      periodicKind: 'monthly'
    }
  },
  {
    path: '/reports/training-center',
    name: 'TrainingCenterReport',
    component: () => import('@/views/reports/TrainingCenterReport.vue'),
    meta: {
      requiresAuth: true,
      title: 'Отчёт Тренинг-центра'
    }
  },
  {
    path: '/reports/schedules',
    name: 'ReportSchedules',
    component: () => import('@/views/reports/ReportSchedules.vue'),
    meta: {
      requiresAuth: true,
      title: 'Рассылки отчётов'
    }
  },

  // === ОСТАЛЬНЫЕ МАРШРУТЫ ===

  // Страница печати PDF рассылки — открывает Chromium в gotenberg по ключу снимка, без входа
  {
    path: '/print/:reportType',
    name: 'PrintReport',
    component: () => import('@/views/print/PrintReport.vue'),
    meta: {
      title: 'Печать отчёта'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: 'Страница не найдена'
    }
  }
]

// Создание роутера
const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

// Глобальные guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Обновляем заголовок страницы
  if (to.meta.title) {
    document.title = `${to.meta.title} | Dashboard System`
  }

  // Проверяем авторизацию для защищенных маршрутов
  if (to.meta.requiresAuth) {
    // Если пользователь не авторизован, проверяем статус
    if (!authStore.isAuthenticated) {
      console.log('🔍 Проверяем авторизацию для защищенного маршрута...')
      await authStore.checkAuthStatus()
    }

    // Если всё ещё не авторизован, перенаправляем на вход
    if (!authStore.isAuthenticated) {
      console.log('❌ Не авторизован, перенаправляем на /login')
      next({
        name: 'Login',
        query: { redirect: to.fullPath }
      })
      return
    }
  }

  // Проверяем маршруты только для гостей (например, страница входа)
  if (to.meta.requiresGuest) {
    if (authStore.isAuthenticated) {
      console.log('✅ Уже авторизован, перенаправляем на дэшборд')
      next({ name: 'Dashboard' })
      return
    }
  }

  next()
})

// Логирование навигации в dev режиме
if (import.meta.env.DEV) {
  router.afterEach((to, from) => {
    console.log(`🧭 Навигация: ${from.path} → ${to.path}`)
  })
}

export default router